import { gqlFetcher, graphql } from '#features/api';
import { getClientSessionId } from '#features/calls/utils/clientSession';

export type CallSessionPayload = {
  id: string;
  chatId: string;
  callerId: string;
  calleeId: string;
  peerUserId: string;
  status: string;
  roomName: string;
  mediaType: string | null;
  endReason: string | null;
  /** ISO instant the call row was created — i.e. when the phone started ringing. */
  createdAt: string | null;
  livekitUrl: string | null;
  token: string | null;
  /**
   * Клиент, которому звонок принадлежит сейчас. Пустой `token` при непустом `ownerSession` значит
   * «звонок открыт на другом устройстве»: подключаться нельзя, можно предложить перенести.
   */
  ownerSession: string | null;
};

/** Downstream error code (call.busy, call.rate.limited, …) preserved for UI mapping. */
export class CallRequestError extends Error {
  readonly errorCode: string;

  constructor(errorCode: string) {
    super(errorCode);
    this.errorCode = errorCode;
  }
}

const CALL_SESSION_FIELDS = `
  id
  chatId
  callerId
  calleeId
  peerUserId
  status
  roomName
  mediaType
  endReason
  createdAt
  livekitUrl
  token
  ownerSession
`;

const startCallDoc = graphql(`
  mutation CallStart($peerUserId: UUID!, $video: Boolean, $sessionId: String) {
    call {
      start(peerUserId: $peerUserId, video: $video, sessionId: $sessionId) {
        __typename
        ... on CallSession {
          id
          chatId
          callerId
          calleeId
          peerUserId
          status
          roomName
          mediaType
          endReason
          createdAt
          livekitUrl
          token
          ownerSession
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

const answerCallDoc = graphql(`
  mutation CallAnswer($callId: UUID!, $sessionId: String) {
    call {
      answer(callId: $callId, sessionId: $sessionId) {
        __typename
        ... on CallSession {
          id
          chatId
          callerId
          calleeId
          peerUserId
          status
          roomName
          mediaType
          endReason
          createdAt
          livekitUrl
          token
          ownerSession
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

const hangupCallDoc = graphql(`
  mutation CallHangup($callId: UUID!) {
    call {
      hangup(callId: $callId) {
        __typename
        ... on CallSession {
          id
          chatId
          callerId
          calleeId
          peerUserId
          status
          roomName
          mediaType
          endReason
          createdAt
          livekitUrl
          token
          ownerSession
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

// Plain string on purpose: `call.active` returns the bare CallSession type (no union),
// mirroring the chatMessagesQuery escape hatch used elsewhere in this folder family.
const activeCallQuery = `
  query CallActive($sessionId: String) {
    call {
      active(sessionId: $sessionId) {
        ${CALL_SESSION_FIELDS}
      }
    }
  }
`;

const claimCallDoc = graphql(`
  mutation CallClaim($callId: UUID!, $sessionId: String!) {
    call {
      claim(callId: $callId, sessionId: $sessionId) {
        __typename
        ... on CallSession {
          id
          chatId
          callerId
          calleeId
          peerUserId
          status
          roomName
          mediaType
          endReason
          createdAt
          livekitUrl
          token
          ownerSession
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

type CallUnionPayload =
  | ({ __typename: 'CallSession' } & CallSessionPayload)
  | { __typename: 'GlobalError'; errorCode?: string | null; errorMessage?: string | null }
  | { __typename?: string };

const unwrapSession = (payload: CallUnionPayload | null | undefined): CallSessionPayload => {
  if (!payload || payload.__typename !== 'CallSession') {
    const errorCode =
      payload && payload.__typename === 'GlobalError'
        ? ((payload as { errorCode?: string | null }).errorCode ?? 'global.error')
        : 'global.error';
    throw new CallRequestError(errorCode);
  }
  return payload as CallSessionPayload;
};

export const startCall = async (
  peerUserId: string,
  video: boolean
): Promise<CallSessionPayload> => {
  const data = (await gqlFetcher(startCallDoc, {
    peerUserId,
    video,
    sessionId: getClientSessionId(),
  })) as {
    call?: { start?: CallUnionPayload | null } | null;
  };
  return unwrapSession(data.call?.start);
};

export const answerCall = async (callId: string): Promise<CallSessionPayload> => {
  const data = (await gqlFetcher(answerCallDoc, {
    callId,
    sessionId: getClientSessionId(),
  })) as {
    call?: { answer?: CallUnionPayload | null } | null;
  };
  return unwrapSession(data.call?.answer);
};

export const hangupCall = async (callId: string): Promise<CallSessionPayload> => {
  const data = (await gqlFetcher(hangupCallDoc, { callId })) as {
    call?: { hangup?: CallUnionPayload | null } | null;
  };
  return unwrapSession(data.call?.hangup);
};

// Resync after a reload knows only the peer's id — pull their display identity
// from the chat (userData arrives with a fully resolved avatar URL).
const callChatPeerQuery = `
  query CallChatPeer($id: UUID!) {
    chat {
      byId(id: $id) {
        userId
        userData {
          displayName
          avatar
        }
      }
    }
  }
`;

export const fetchCallChatPeer = async (
  chatId: string
): Promise<{ displayName: string | null; avatar: string | null } | null> => {
  const data = (await gqlFetcher(callChatPeerQuery, { id: chatId })) as {
    chat?: {
      byId?: {
        userData?: { displayName?: string | null; avatar?: string | null } | null;
      } | null;
    } | null;
  };
  const userData = data.chat?.byId?.userData;
  if (!userData) {
    return null;
  }
  return { displayName: userData.displayName ?? null, avatar: userData.avatar ?? null };
};

/** Перенести живой звонок на этот клиент — другое устройство отпустит комнату. */
export const claimCall = async (callId: string): Promise<CallSessionPayload> => {
  const data = (await gqlFetcher(claimCallDoc, {
    callId,
    sessionId: getClientSessionId(),
  })) as {
    call?: { claim?: CallUnionPayload | null } | null;
  };
  return unwrapSession(data.call?.claim);
};

export const fetchActiveCall = async (): Promise<CallSessionPayload | null> => {
  const data = (await gqlFetcher(activeCallQuery, { sessionId: getClientSessionId() })) as {
    call?: { active?: CallSessionPayload | null } | null;
  };
  return data.call?.active ?? null;
};
